/**
 * Quotation (Client Notes) Service
 * Handles API calls for quotation functionality
 */

import axiosInstance from "../utils/axios";

// API Endpoints
const API_ENDPOINTS = {
  QUOTATION: "api/Quotation",
};

/**
 * Get lead quotations
 * @param {Object|string} requestData - The request query parameters or Lead ID string
 * @returns {Promise<Object>} Response object with success, data, totalRecords, and message
 */
export const getLeadQuotations = async (requestData) => {
  try {
    const payload = typeof requestData === "string" ? { leadId: requestData } : requestData;
    if (!payload || !payload.leadId) {
      throw new Error("Lead ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/GetLeadQuotations`,
      payload,
      {
        showLoader: false,
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing quotations JSON:", e);
          data = [];
        }
      }

      return {
        success: true,
        data: data || [],
        totalRecords: responseData.totalRecords || 0,
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch quotations");
    }
  } catch (error) {
    console.error("Error in getLeadQuotations:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch quotations",
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Save or Update a Lead Quotation
 * @param {Object} requestData - The quotation fields
 * @returns {Promise<Object>} Response object
 */
export const upsertLeadQuotation = async (requestData) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertLeadQuotation`,
      requestData
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing upserted quotation:", e);
        }
      }

      return {
        success: true,
        data: data,
        message: responseData.message || "Quotation saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save quotation");
    }
  } catch (error) {
    console.error("Error in upsertLeadQuotation:", error);
    return {
      success: false,
      error: error.message || "Failed to save quotation",
    };
  }
};

/**
 * Delete a Lead Quotation
 * @param {string} id - The quotation ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteLeadQuotation = async (id) => {
  try {
    if (!id) {
      throw new Error("Quotation ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteLeadQuotation`,
      null,
      {
        params: { id },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Quotation deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete quotation");
    }
  } catch (error) {
    console.error("Error in deleteLeadQuotation:", error);
    return {
      success: false,
      error: error.message || "Failed to delete quotation",
    };
  }
};

/**
 * Get quotation categories
 * @param {string} search - Search query to filter categories
 * @returns {Promise<Object>} Response object
 */
export const getQuotationCategories = async (search = "") => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUOTATION}/GetQuotationCategories`,
      {
        params: { search, pageSize: 100 },
        showLoader: false,
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing categories:", e);
          data = [];
        }
      }

      return {
        success: true,
        data: data || [],
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch categories");
    }
  } catch (error) {
    console.error("Error in getQuotationCategories:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch categories",
      data: [],
    };
  }
};

/**
 * Add or Update a Quotation Category
 * @param {string} name - Name of the category
 * @param {number|null} id - ID for update, or null for insert
 * @returns {Promise<Object>} Response object
 */
export const upsertQuotationCategory = async (name, id = null) => {
  try {
    if (!name || name.trim() === "") {
      throw new Error("Category name is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertQuotationCategory`,
      { id, name: name.trim() }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing category data:", e);
        }
      }

      return {
        success: true,
        data: data,
        message: responseData.message || "Category saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save category");
    }
  } catch (error) {
    console.error("Error in upsertQuotationCategory:", error);
    return {
      success: false,
      error: error.message || "Failed to save category",
    };
  }
};

/**
 * Delete a Quotation Category
 * @param {number|string} id - The category ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteQuotationCategory = async (id) => {
  try {
    if (!id) {
      throw new Error("Category ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteQuotationCategory`,
      null,
      {
        params: { id },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Category deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete category");
    }
  } catch (error) {
    console.error("Error in deleteQuotationCategory:", error);
    return {
      success: false,
      error: error.message || "Failed to delete category",
    };
  }
};

/**
 * Get quotation work statuses
 * @returns {Promise<Object>} Response object
 */
export const getQuotationWorkStatuses = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUOTATION}/GetQuotationWorkStatuses`,
      {
        showLoader: false,
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing work statuses:", e);
          data = [];
        }
      }

      return {
        success: true,
        data: data || [],
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch work statuses");
    }
  } catch (error) {
    console.error("Error in getQuotationWorkStatuses:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch work statuses",
      data: [],
    };
  }
};

/**
 * Get quotation cost statuses
 * @returns {Promise<Object>} Response object
 */
export const getQuotationCostStatuses = async () => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUOTATION}/GetQuotationCostStatuses`,
      {
        showLoader: false,
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing cost statuses:", e);
          data = [];
        }
      }

      return {
        success: true,
        data: data || [],
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch cost statuses");
    }
  } catch (error) {
    console.error("Error in getQuotationCostStatuses:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch cost statuses",
      data: [],
    };
  }
};

/**
 * Get quotation wish items
 * @param {string} search - Search query to filter wish items
 * @returns {Promise<Object>} Response object
 */
export const getQuotationWishItems = async (search = "") => {
  try {
    const response = await axiosInstance.get(
      `${API_ENDPOINTS.QUOTATION}/GetQuotationWishItems`
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing wish items:", e);
          data = [];
        }
      }

      return {
        success: true,
        data: data || [],
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch wish items");
    }
  } catch (error) {
    console.error("Error in getQuotationWishItems:", error);
    return {
      success: false,
      error: error.message || "Failed to fetch wish items",
      data: [],
    };
  }
};

/**
 * Add or Update a Quotation Wish Item
 * @param {string} name - Name of the wish item
 * @param {number|null} id - ID for update, or null for insert
 * @returns {Promise<Object>} Response object
 */
export const upsertQuotationWishItem = async (name, id = null) => {
  try {
    if (!name || name.trim() === "") {
      throw new Error("Wish item name is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertQuotationWishItem`,
      { id, wishItemName: name.trim() }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing wish item data:", e);
        }
      }

      return {
        success: true,
        data: data,
        message: responseData.message || "Wish item saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save wish item");
    }
  } catch (error) {
    console.error("Error in upsertQuotationWishItem:", error);
    return {
      success: false,
      error: error.message || "Failed to save wish item",
    };
  }
};

/**
 * Delete a Quotation Wish Item
 * @param {number|string} id - The wish item ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteQuotationWishItem = async (id) => {
  try {
    if (!id) {
      throw new Error("Wish item ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteQuotationWishItem`,
      null,
      {
        params: { id },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Wish item deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete wish item");
    }
  } catch (error) {
    console.error("Error in deleteQuotationWishItem:", error);
    return {
      success: false,
      error: error.message || "Failed to delete wish item",
    };
  }
};

/**
 * Update Lead Quotation Status
 * @param {Object} payload - { id, workStatusId, costStatusId, userId }
 * @returns {Promise<Object>} Response object
 */
export const updateLeadQuotationStatus = async (payload) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpdateLeadQuotationStatus`,
      payload
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Status updated successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to update status");
    }
  } catch (error) {
    console.error("Error in updateLeadQuotationStatus:", error);
    return {
      success: false,
      error: error.message || "Failed to update status",
    };
  }
};

/**
 * Get job quotations (client notes)
 * @param {Object} requestData - { jobId, search, pageNumber, pageSize }
 * @returns {Promise<Object>} Response object
 */
export const getJobQuotations = async (requestData) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/GetJobQuotations`,
      requestData,
      { showLoader: false }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) { data = []; }
      }
      return {
        success: true,
        data: data || [],
        totalRecords: responseData.totalRecords || 0,
        message: responseData.message,
      };
    } else {
      throw new Error(responseData.message || "Failed to fetch job quotations");
    }
  } catch (error) {
    console.error("Error in getJobQuotations:", error);
    return { success: false, error: error.message || "Failed to fetch job quotations", data: [], totalRecords: 0 };
  }
};

/**
 * Save or Update a Job Quotation (client note)
 * @param {Object} requestData - Quotation fields with jobId
 * @returns {Promise<Object>} Response object
 */
export const upsertJobQuotation = async (requestData) => {
  try {
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertJobQuotation`,
      requestData
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try { data = JSON.parse(data); } catch (e) { /* noop */ }
      }
      return { success: true, data, message: responseData.message || "Quotation saved successfully" };
    } else {
      throw new Error(responseData.message || "Failed to save job quotation");
    }
  } catch (error) {
    console.error("Error in upsertJobQuotation:", error);
    return { success: false, error: error.message || "Failed to save job quotation" };
  }
};

/**
 * Delete a Job Quotation (client note)
 * @param {string} id - The quotation ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteJobQuotation = async (id) => {
  try {
    if (!id) throw new Error("Quotation ID is required");

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteJobQuotation`,
      null,
      { params: { id } }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return { success: true, message: responseData.message || "Quotation deleted successfully" };
    } else {
      throw new Error(responseData.message || "Failed to delete job quotation");
    }
  } catch (error) {
    console.error("Error in deleteJobQuotation:", error);
    return { success: false, error: error.message || "Failed to delete job quotation" };
  }
};

/**
 * Add or Update a Quotation Work Status
 * @param {string} name - Name of the status
 * @param {number|null} id - ID for update, or null for insert
 * @returns {Promise<Object>} Response object
 */
export const upsertQuotationWorkStatus = async (name, id = null) => {
  try {
    if (!name || name.trim() === "") {
      throw new Error("Status name is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertQuotationWorkStatus`,
      { id, statusName: name.trim(), displayOrder: 0, badgeColor: "#64748b" }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing work status data:", e);
        }
      }

      return {
        success: true,
        data: data,
        message: responseData.message || "Work status saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save work status");
    }
  } catch (error) {
    console.error("Error in upsertQuotationWorkStatus:", error);
    return {
      success: false,
      error: error.message || "Failed to save work status",
    };
  }
};

/**
 * Delete a Quotation Work Status
 * @param {number|string} id - The work status ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteQuotationWorkStatus = async (id) => {
  try {
    if (!id) {
      throw new Error("Work status ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteQuotationWorkStatus`,
      null,
      {
        params: { id },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Work status deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete work status");
    }
  } catch (error) {
    console.error("Error in deleteQuotationWorkStatus:", error);
    return {
      success: false,
      error: error.message || "Failed to delete work status",
    };
  }
};

/**
 * Add or Update a Quotation Cost Status
 * @param {string} name - Name of the cost status
 * @param {number|null} id - ID for update, or null for insert
 * @returns {Promise<Object>} Response object
 */
export const upsertQuotationCostStatus = async (name, id = null) => {
  try {
    if (!name || name.trim() === "") {
      throw new Error("Cost status name is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/UpsertQuotationCostStatus`,
      { id, statusName: name.trim(), displayOrder: 0, badgeColor: "#64748b" }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      let data = responseData.data;
      if (typeof data === "string") {
        try {
          data = JSON.parse(data);
        } catch (e) {
          console.error("Error parsing cost status data:", e);
        }
      }

      return {
        success: true,
        data: data,
        message: responseData.message || "Cost status saved successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to save cost status");
    }
  } catch (error) {
    console.error("Error in upsertQuotationCostStatus:", error);
    return {
      success: false,
      error: error.message || "Failed to save cost status",
    };
  }
};

/**
 * Delete a Quotation Cost Status
 * @param {number|string} id - The cost status ID to delete
 * @returns {Promise<Object>} Response object
 */
export const deleteQuotationCostStatus = async (id) => {
  try {
    if (!id) {
      throw new Error("Cost status ID is required");
    }

    const response = await axiosInstance.post(
      `${API_ENDPOINTS.QUOTATION}/DeleteQuotationCostStatus`,
      null,
      {
        params: { id },
      }
    );

    const responseData = response.data;

    if (responseData.statusCode === 200 && responseData.status === "Success") {
      return {
        success: true,
        message: responseData.message || "Cost status deleted successfully",
      };
    } else {
      throw new Error(responseData.message || "Failed to delete cost status");
    }
  } catch (error) {
    console.error("Error in deleteQuotationCostStatus:", error);
    return {
      success: false,
      error: error.message || "Failed to delete cost status",
    };
  }
};

export default {
  getLeadQuotations,
  upsertLeadQuotation,
  deleteLeadQuotation,
  getQuotationCategories,
  upsertQuotationCategory,
  deleteQuotationCategory,
  getQuotationWishItems,
  upsertQuotationWishItem,
  deleteQuotationWishItem,
  getQuotationWorkStatuses,
  getQuotationCostStatuses,
  updateLeadQuotationStatus,
  getJobQuotations,
  upsertJobQuotation,
  deleteJobQuotation,
  upsertQuotationWorkStatus,
  deleteQuotationWorkStatus,
  upsertQuotationCostStatus,
  deleteQuotationCostStatus,
};
